import { useState, useEffect } from 'react';
import { 
  Route, Plus, Calendar, Clock, Users, MapPin, ArrowRight, ArrowDown,
  Trash2, Loader2, ChevronDown, ChevronUp, Star, GripVertical, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { driverRouteAPI, routeBookingAPI } from '../lib/api';
import { toast } from 'sonner';
import RouteMap from './RouteMap';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon', fullLabel: 'Monday' },
  { value: 1, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 2, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 3, label: 'Thu', fullLabel: 'Thursday' },
  { value: 4, label: 'Fri', fullLabel: 'Friday' },
  { value: 5, label: 'Sat', fullLabel: 'Saturday' },
  { value: 6, label: 'Sun', fullLabel: 'Sunday' },
];

const DriverRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [expandedMap, setExpandedMap] = useState(null);
  const [formData, setFormData] = useState({
    route_name: '',
    origin_address: '',
    destination_address: '',
    departure_time: '08:00',
    days_of_week: [],
    available_seats: 4,
    price_per_seat: '',
    notes: '',
  });
  const [intermediateStops, setIntermediateStops] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, bookingsRes] = await Promise.all([
        driverRouteAPI.getMyRoutes(),
        routeBookingAPI.getAll(),
      ]);
      setRoutes(routesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    
    if (formData.days_of_week.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    
    if (!formData.origin_address || !formData.destination_address) {
      toast.error('Please enter both origin and destination');
      return;
    }
    
    setSubmitting(true);
    try {
      const stops = intermediateStops
        .filter(s => s.address.trim())
        .map(s => ({ address: s.address.trim() }));
      
      const payload = {
        ...formData,
        stops: stops.length > 0 ? stops : null,
        price_per_seat: formData.price_per_seat ? parseFloat(formData.price_per_seat) : null,
        available_seats: parseInt(formData.available_seats),
      };
      
      await driverRouteAPI.create(payload);
      toast.success('Route created successfully!');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create route');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm('Are you sure you want to delete this route?')) return;
    
    try {
      await driverRouteAPI.delete(routeId);
      toast.success('Route deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete route');
    }
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort()
    }));
  };

  const addStop = () => {
    setIntermediateStops(prev => [...prev, { id: Date.now(), address: '' }]);
  };

  const removeStop = (id) => {
    setIntermediateStops(prev => prev.filter(s => s.id !== id));
  };

  const updateStop = (id, address) => {
    setIntermediateStops(prev => prev.map(s => s.id === id ? { ...s, address } : s));
  };

  const resetForm = () => {
    setFormData({
      route_name: '',
      origin_address: '',
      destination_address: '',
      departure_time: '08:00',
      days_of_week: [],
      available_seats: 4,
      price_per_seat: '',
      notes: '',
    });
    setIntermediateStops([]);
  };

  const getRouteBookings = (routeId) => {
    return bookings.filter(b => b.route_id === routeId && b.status !== 'cancelled');
  };

  const formatDays = (days) => {
    return days
      .sort((a, b) => a - b)
      .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label)
      .join(', ');
  };

  const renderStopsList = (route) => {
    const stops = route.stops || [];
    if (stops.length <= 2) return null;
    
    return (
      <div className="flex items-start gap-2 mt-2">
        <div className="flex flex-col items-center gap-0.5 pt-1">
          {stops.map((_, i) => (
            <div key={`dot-${i}-${stops[i]?.address}`}>
              <div className={`w-2.5 h-2.5 rounded-full ${i === 0 ? 'bg-green-500' : i === stops.length - 1 ? 'bg-red-500' : 'bg-orange-400'}`} />
              {i < stops.length - 1 && <div className="w-px h-3 bg-zinc-300 mx-auto" />}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1 text-xs text-zinc-500">
          {stops.map((stop, i) => (
            <div key={`stop-${i}-${stop.address}`} className="leading-tight py-0.5">
              <span className="font-medium text-zinc-700">{String.fromCharCode(65 + i)}</span>
              {' '}{stop.address}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Scheduled Routes
          </h2>
          <p className="text-sm text-zinc-500">Create multi-stop routes that riders can book</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-route-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Route
        </Button>
      </div>

      {/* Routes List */}
      {routes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Route className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 mb-2">No routes yet</h3>
          <p className="text-zinc-600 mb-4">Create a scheduled route so riders can book with you</p>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Route
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {routes.map((route) => {
            const routeBookings = getRouteBookings(route.id);
            const isExpanded = expandedRoute === route.id;
            const isMapExpanded = expandedMap === route.id;
            const hasStops = route.stops && route.stops.length > 2;
            
            return (
              <div
                key={route.id}
                className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
                data-testid={`route-card-${route.id}`}
              >
                {/* Route Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-zinc-900">{route.route_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          route.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-zinc-100 text-zinc-600'
                        }`}>
                          {route.status}
                        </span>
                        {hasStops && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            {route.stops.length} stops
                          </span>
                        )}
                      </div>
                      
                      {/* Simple origin → destination */}
                      <div className="flex items-center gap-2 text-sm text-zinc-600 mb-2">
                        <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{route.origin_address}</span>
                        <ArrowRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                        <span>{route.destination_address}</span>
                      </div>
                      
                      {/* Multi-stop details */}
                      {hasStops && renderStopsList(route)}
                      
                      <div className="flex flex-wrap gap-4 text-sm mt-2">
                        <div className="flex items-center gap-1.5 text-zinc-500">
                          <Clock className="w-4 h-4" />
                          <span>{route.departure_time}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-500">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDays(route.days_of_week)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-500">
                          <Users className="w-4 h-4" />
                          <span>{route.booked_seats}/{route.available_seats} seats</span>
                        </div>
                        {route.zone_name && (
                          <span className="text-blue-600">{route.zone_name}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-zinc-900">${route.price_per_seat.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500">per seat</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedRoute(isExpanded ? null : route.id)}
                        className="text-zinc-600"
                      >
                        {routeBookings.length > 0 ? (
                          <>
                            <span>{routeBookings.length} booking{routeBookings.length !== 1 ? 's' : ''}</span>
                            {isExpanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                          </>
                        ) : (
                          <span className="text-zinc-400">No bookings yet</span>
                        )}
                      </Button>
                      {route.stops?.some(s => s.lat != null) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedMap(isMapExpanded ? null : route.id)}
                          className="text-orange-600 hover:text-orange-700"
                          data-testid={`toggle-map-${route.id}`}
                        >
                          <MapPin className="w-4 h-4 mr-1" />
                          {isMapExpanded ? 'Hide Map' : 'Show Map'}
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRoute(route.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-route-${route.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Route Map */}
                {isMapExpanded && route.stops && (
                  <div className="border-t border-zinc-100 p-4">
                    <RouteMap stops={route.stops} height="280px" />
                  </div>
                )}
                
                {/* Bookings Expansion */}
                {isExpanded && routeBookings.length > 0 && (
                  <div className="border-t border-zinc-100 bg-zinc-50 p-4">
                    <h4 className="text-sm font-medium text-zinc-700 mb-3">Upcoming Bookings</h4>
                    <div className="space-y-2">
                      {routeBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-zinc-100"
                        >
                          <div>
                            <p className="font-medium text-zinc-900">{booking.user_name}</p>
                            <p className="text-sm text-zinc-500">
                              {new Date(booking.travel_date).toLocaleDateString()} • {booking.seats_requested} seat{booking.seats_requested !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-zinc-900">${booking.total_price.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              'bg-zinc-100 text-zinc-600'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Route Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Scheduled Route</DialogTitle>
            <DialogDescription>
              Set up a multi-stop recurring route that riders can book
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateRoute} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="route_name">Route Name</Label>
              <Input
                id="route_name"
                placeholder="e.g., Morning Commute to Scott AFB"
                value={formData.route_name}
                onChange={(e) => setFormData(prev => ({ ...prev, route_name: e.target.value }))}
                required
                data-testid="route-name-input"
              />
            </div>
            
            {/* Multi-stop route builder */}
            <div className="space-y-2">
              <Label>Route Stops</Label>
              <div className="space-y-2 bg-zinc-50 rounded-lg p-3">
                {/* Origin */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">A</div>
                  <Input
                    placeholder="Starting point (must be in service area)"
                    value={formData.origin_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, origin_address: e.target.value }))}
                    required
                    data-testid="origin-input"
                    className="flex-1"
                  />
                </div>
                
                {/* Intermediate stops */}
                {intermediateStops.map((stop, i) => (
                  <div key={stop.id} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-orange-400 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {String.fromCharCode(66 + i)}
                    </div>
                    <Input
                      placeholder={`Stop ${i + 1} address`}
                      value={stop.address}
                      onChange={(e) => updateStop(stop.id, e.target.value)}
                      className="flex-1"
                      data-testid={`stop-input-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeStop(stop.id)}
                      className="w-7 h-7 rounded-full text-zinc-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors flex-shrink-0"
                      data-testid={`remove-stop-${i}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {/* Add stop button */}
                <button
                  type="button"
                  onClick={addStop}
                  className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium py-1.5 px-2 rounded-lg hover:bg-orange-50 transition-colors w-full"
                  data-testid="add-stop-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add intermediate stop
                </button>
                
                {/* Destination */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {String.fromCharCode(66 + intermediateStops.length)}
                  </div>
                  <Input
                    placeholder="End point (must be in service area)"
                    value={formData.destination_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, destination_address: e.target.value }))}
                    required
                    data-testid="destination-input"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="departure_time">Departure Time</Label>
                <Input
                  id="departure_time"
                  type="time"
                  value={formData.departure_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, departure_time: e.target.value }))}
                  required
                  data-testid="departure-time-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="available_seats">Available Seats</Label>
                <Input
                  id="available_seats"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.available_seats}
                  onChange={(e) => setFormData(prev => ({ ...prev, available_seats: e.target.value }))}
                  required
                  data-testid="seats-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Days of Week</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      formData.days_of_week.includes(day.value)
                        ? 'bg-orange-500 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                    data-testid={`day-${day.label.toLowerCase()}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="price_per_seat">
                Price Per Seat (optional)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                <Input
                  id="price_per_seat"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Leave empty for zone-based pricing"
                  value={formData.price_per_seat}
                  onChange={(e) => setFormData(prev => ({ ...prev, price_per_seat: e.target.value }))}
                  className="pl-7"
                  data-testid="price-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Leave empty to use automatic zone-based pricing
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any additional info for riders..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="notes-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowCreateDialog(false); resetForm(); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={submitting}
                data-testid="submit-route-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Route'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverRoutes;
